// Block D: Import CSV on Contacts. Parsing happens here, in the browser, so the preview can show
// exactly what will be sent before anything is saved.
//
//   parseCsv(text)            rows of cells; quoted fields, "" escapes, commas and line breaks
//                             inside quotes, CRLF, a BOM, and ; or tab separated files
//   contactsFromCsv(text)     { rows, columns, problems }: one { name, email, phone, company, tags }
//                             per line, read by the header row's names (any order, any case)
//   importBatches(rows)       the rows cut into requests small enough to pass the edge, which refuses
//                             a body over about 8 KB (SizeRestrictions_BODY, the AWS managed rule)

export const IMPORT_MAX_ROWS = 2000;
export const BATCH_BYTES = 6000;

function detectSeparator(text) {
  const first = text.split(/\r?\n/, 1)[0] || '';
  const count = (c) => first.split(c).length - 1;
  const counts = [[',', count(',')], [';', count(';')], ['\t', count('\t')]];
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ',';
}

export function parseCsv(input) {
  const text = String(input || '').replace(/^﻿/, '');
  const sep = detectSeparator(text);
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 1; } else quoted = false;
      } else cell += c;
    } else if (c === '"' && cell === '') {
      quoted = true;
    } else if (c === sep) {
      row.push(cell); cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += c;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((x) => String(x).trim() !== ''));
}

// Header names people actually use, mapped to the five columns Aidey imports.
const HEADER = {
  name: ['name', 'full name', 'fullname', 'contact', 'contact name', 'client', 'client name'],
  first: ['first name', 'firstname', 'given name'],
  last: ['last name', 'lastname', 'surname', 'family name'],
  email: ['email', 'e-mail', 'email address', 'e-mail address', 'mail'],
  phone: ['phone', 'phone number', 'mobile', 'telephone', 'tel', 'mobile number'],
  company: ['company', 'organisation', 'organization', 'business', 'company name'],
  tags: ['tags', 'tag', 'labels', 'groups', 'category'],
};

export function contactsFromCsv(text) {
  const grid = parseCsv(text);
  if (!grid.length) return { rows: [], columns: [], problems: ['The file is empty.'] };
  const head = grid[0].map((h) => String(h).trim().toLowerCase());
  const col = {};
  for (const [key, names] of Object.entries(HEADER)) {
    const i = head.findIndex((h) => names.includes(h));
    if (i >= 0) col[key] = i;
  }
  const problems = [];
  if (col.name === undefined && col.first === undefined) {
    problems.push('No "name" column was found. The first row must name the columns: name, email, phone, company, tags.');
    return { rows: [], columns: [], problems };
  }
  const at = (r, key) => (col[key] === undefined ? '' : String(r[col[key]] ?? '').trim());
  const rows = grid.slice(1).map((r) => {
    const name = at(r, 'name') || [at(r, 'first'), at(r, 'last')].filter(Boolean).join(' ');
    return {
      name,
      email: at(r, 'email'),
      phone: at(r, 'phone'),
      company: at(r, 'company'),
      tags: at(r, 'tags').split(/[;|,]/).map((t) => t.trim()).filter(Boolean),
    };
  });
  if (rows.length > IMPORT_MAX_ROWS) {
    problems.push(`This file has ${rows.length.toLocaleString('en-GB')} rows. Import at most ${IMPORT_MAX_ROWS.toLocaleString('en-GB')} at a time.`);
  }
  const columns = Object.keys(col).filter((k) => k !== 'first' && k !== 'last');
  if (col.first !== undefined && !columns.includes('name')) columns.unshift('name');
  return { rows, columns, problems };
}

// What the preview says about each row before importing: a duplicate (of a contact already here,
// or of an earlier row in the file), a row that will fail, or a new contact. The server applies
// the same rules, and its counts are the ones reported afterwards.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function previewRows(rows, existingContacts = []) {
  const existing = new Set(existingContacts.map((c) => String(c.email || '').trim().toLowerCase()).filter(Boolean));
  const seen = new Set();
  return rows.map((r) => {
    if (!r.name) return { ...r, status: 'fail', reason: 'No name' };
    if (r.email && !EMAIL_RE.test(r.email)) return { ...r, status: 'fail', reason: 'Email is not valid' };
    const key = r.email.toLowerCase();
    if (key && existing.has(key)) return { ...r, status: 'duplicate', reason: 'Already a contact' };
    if (key && seen.has(key)) return { ...r, status: 'duplicate', reason: 'Repeated in this file' };
    if (key) seen.add(key);
    return { ...r, status: 'new' };
  });
}

// UTF-8 bytes, not characters: an accented name is two bytes a letter on the wire.
const bytes = (s) => encodeURIComponent(s).replace(/%[0-9A-F]{2}/g, 'x').length;

export function importBatches(rows, maxBytes = BATCH_BYTES) {
  const batches = [];
  let batch = [];
  let size = 12;   // {"rows":[]}
  for (const r of rows) {
    const n = bytes(JSON.stringify(r)) + 1;
    if (batch.length && size + n > maxBytes) { batches.push(batch); batch = []; size = 12; }
    batch.push(r); size += n;
  }
  if (batch.length) batches.push(batch);
  return batches;
}
